/**
 * The world high-score tables on the optional server (`/api/scores`, docs/spec/06 §7). Like the
 * Daily board, the game never depends on it: no server means null and 'retry', nothing more.
 */

import type { Tables } from '../game/hiscores';
import { parseWorldTables } from '../game/hiscores';
import type { Outgoing } from './scoreBook';

/** Long enough for a stopped machine to wake: the host starts it on the first request. */
const TIMEOUT_MS = 12000;

/** 'retry': not taken this time (no answer, restarting, busy); 'refused': never will be. */
export type PostResult = 'ok' | 'retry' | 'refused';

async function call(url: string, init?: RequestInit): Promise<Response | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** The world tables, best first; null when there is no server or it did not answer. */
export async function fetchWorldTables(): Promise<Tables | null> {
  const res = await call('api/scores');
  if (!res?.ok) return null;
  try {
    const body = (await res.json()) as { tables?: unknown };
    if (typeof body.tables !== 'object' || body.tables === null) return null;
    return parseWorldTables(JSON.stringify(body.tables));
  } catch {
    // A static host answers with the page itself: no server here.
    return null;
  }
}

/**
 * Send one score with its game's log; the server replays the log and takes the score only if the
 * game made it. Sending the same id twice is safe: the server keeps it once.
 */
export async function postWorldScore(o: Outgoing): Promise<PostResult> {
  const res = await call('api/scores', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ table: o.table, player: o.player, ...o.entry, proof: o.log }),
  });
  if (!res) return 'retry';
  if (res.ok) return 'ok';
  // Too many, too slow, or the server (or the host in front of it) is not ready: try again later.
  if (res.status === 408 || res.status === 429 || res.status >= 500) return 'retry';
  // No such endpoint: a static host, or an older server. The score stays queued for one that has it.
  if (res.status === 404 || res.status === 405) return 'retry';
  return 'refused';
}
